import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import Edit from '../edit/Edit';
import styles from './hoc.module.scss';

const {
  inHeader,
  header,
  headerContainer,
  panel,
  dashedPanel,
  section,
  subHeader,
  count: countStyle,
  hiddenBadge,
  placeholder: placeholderStyle,
  addLink,
} = styles;

class PortfolioHOC extends PureComponent {
  static propTypes = {
    // Required Props
    ChildComponent: PropTypes.func.isRequired,
    userData: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    user: PropTypes.object.isRequired,
    isOwner: PropTypes.bool.isRequired,
    isEmpty: PropTypes.bool.isRequired,
    title: PropTypes.string,

    // Optional Props
    isHeader: PropTypes.bool,
    updateKey: PropTypes.string,
    placeholder: PropTypes.string,
    loggedInUser: PropTypes.object,
    componentID: PropTypes.string, // Used to identify components that have a duplicated updateKey
    optionalFunction: PropTypes.func,
    getFieldError: PropTypes.func,
    disableEditMode: PropTypes.bool,
    permanentInformation: PropTypes.object,
    // 'card' is a standalone card; 'section' sits inside another card, and
    // 'lead' is the first section in a card, titled like a card.
    variant: PropTypes.oneOf(['card', 'section', 'lead']),
    count: PropTypes.number,
    titleIcon: PropTypes.node,
    addLabel: PropTypes.string,
  };

  static defaultProps = {
    disableEditMode: false,
    variant: 'card',
  };

  constructor(props) {
    super(props);

    this.defaultState = {
      editMode: false,
      [props.updateKey]: props.userData,
    };

    this.state = cloneDeep(this.defaultState);
  }

  componentDidUpdate(prevProps) {
    const { userData: oldUserData } = prevProps;
    const { userData: newUserData } = this.props;
    if (!isEqual(oldUserData, newUserData)) this.updateData(newUserData);
  }

  updateData = (newUserData) => {
    const { updateKey } = this.props;

    this.defaultState = { ...this.defaultState, [updateKey]: newUserData };

    this.setState({
      [updateKey]: cloneDeep(newUserData),
    });
  };

  toggleEditMode = () => this.setState({ editMode: !this.state.editMode });

  cancelEdit = () => this.setState(cloneDeep(this.defaultState));

  render() {
    const {
      isHeader,
      user,
      title,
      loggedInUser,
      placeholder,
      isEmpty,
      ChildComponent,
      updateKey,
      optionalFunction,
      getFieldError,
      disableEditMode,
      isOwner,
      componentID,
      permanentInformation,
      variant,
      count,
      titleIcon,
      addLabel,
    } = this.props;
    // An owner previewing as a client may still have edit mode and an unsaved
    // draft stored, so the preview shows the saved data.
    const editMode = isOwner && this.state.editMode;
    const userData = isOwner ? this.state[updateKey] : this.props.userData;
    const fieldError = getFieldError ? getFieldError(userData) : null;

    const headerChild = (
      <div id={`portfolio-${componentID || updateKey}`} className={inHeader}>
        <ChildComponent
          data={userData}
          editMode={editMode}
          updateData={(data) => this.setState({ [updateKey]: data })}
          user={user}
          isOwner={isOwner}
          toggleEditMode={this.toggleEditMode}
          loggedInUser={loggedInUser}
          optionalFunction={optionalFunction}
        />
        {isOwner && !disableEditMode && (
          <Edit
            isHeader
            cancelEdit={this.cancelEdit}
            editMode={editMode}
            updateKey={updateKey}
            componentID={componentID}
            toggleEditMode={this.toggleEditMode}
            updates={{ [updateKey]: userData }}
            saveDisabled={!!fieldError}
            owner={user}
            editLabel="Edit name"
          />
        )}
      </div>
    );

    // Edit checks the signed-in user itself, so also gate on isOwner: an owner
    // previewing as a client must not see the pencils.
    const edit = isOwner && !disableEditMode && (
      <Edit
        cancelEdit={this.cancelEdit}
        editMode={editMode}
        updateKey={updateKey}
        componentID={componentID}
        toggleEditMode={this.toggleEditMode}
        updates={{ [updateKey]: userData }}
        saveDisabled={!!fieldError}
        owner={user}
        editLabel={title ? `Edit ${title.toLowerCase()}` : undefined}
      />
    );

    const isSection = variant === 'section' || variant === 'lead';
    const isLead = variant === 'lead';
    const showOwnerEmpty = isEmpty && !editMode && !disableEditMode;
    const sectionId = `portfolio-${componentID || updateKey}`;
    const TitleTag = isSection && !isLead ? 'h3' : 'h2';

    const titleRow = (
      <div className={headerContainer}>
        <TitleTag className={isSection && !isLead ? subHeader : header}>
          {title}
          {typeof count === 'number' && count > 0 && (
            <span className={countStyle}>· {count}</span>
          )}
          {titleIcon}
        </TitleTag>
        {showOwnerEmpty && !isSection ? (
          <span className={hiddenBadge}>Hidden until added</span>
        ) : (
          edit
        )}
      </div>
    );

    // Owners see empty sections as a dashed card; clients never see them.
    const ownerEmpty = (
      <p className={placeholderStyle}>
        {placeholder}{' '}
        <button type="button" className={addLink} onClick={this.toggleEditMode}>
          {addLabel || 'Add'}
        </button>
      </p>
    );

    const bodyChild = (
      <div
        id={sectionId}
        className={isSection ? section : showOwnerEmpty ? dashedPanel : panel}
      >
        {titleRow}
        {permanentInformation && permanentInformation.body}
        {showOwnerEmpty ? (
          ownerEmpty
        ) : (
          <ChildComponent
            data={userData}
            editMode={editMode}
            updateData={(data) => this.setState({ [updateKey]: data })}
            user={user}
            isOwner={isOwner}
            loggedInUser={loggedInUser}
            optionalFunction={optionalFunction}
          />
        )}
      </div>
    );

    return !isOwner && isEmpty ? null : isHeader ? headerChild : bodyChild;
  }
}

export { PortfolioHOC };
export default PortfolioHOC;
